import type { MatchAnswerKind, MatchRecord, PossibleMatch, SetAsideFile, TriggeringField } from './matchesApi';

export type DisplayCategory = 'same_id_name' | 'same_id_dob' | 'id_conflict' | 'missing_id';

export type DisplayGroup = { category: DisplayCategory; header: string; matches: PossibleMatch[] };

const GROUP_ORDER: { category: DisplayCategory; header: string }[] = [
  { category: 'same_id_name', header: 'Same ID • Names Differ' },
  { category: 'same_id_dob', header: 'Same ID • Date of Birth Differs' },
  { category: 'id_conflict', header: 'Same Name • IDs Differ' },
  { category: 'missing_id', header: 'Same Name • Missing ID' },
];

const CATEGORY_BY_FIELD: Record<TriggeringField, DisplayCategory> = {
  name: 'same_id_name',
  dob: 'same_id_dob',
  id: 'id_conflict',
  'missing-id': 'missing_id',
};

export const SHOW_FIRST = 3;

export function categoryOf(match: PossibleMatch): DisplayCategory {
  return CATEGORY_BY_FIELD[match.triggeringField] ?? 'id_conflict';
}

export function openMatches(matches: PossibleMatch[]): PossibleMatch[] {
  return matches.filter((m) => m.answer === null);
}

export function groupOpenMatches(matches: PossibleMatch[]): DisplayGroup[] {
  const open = openMatches(matches);
  return GROUP_ORDER.map(({ category, header }) => ({
    category,
    header,
    matches: open.filter((m) => categoryOf(m) === category),
  })).filter((group) => group.matches.length > 0);
}

export function reviewedMatches(matches: PossibleMatch[]): PossibleMatch[] {
  return matches
    .filter((m) => m.answer !== null)
    .sort((a, b) => (a.answer?.decidedAt ?? '').localeCompare(b.answer?.decidedAt ?? ''));
}

export function visibleMatches(open: PossibleMatch[], expanded: boolean): { shown: PossibleMatch[]; hidden: number } {
  if (expanded || open.length <= SHOW_FIRST) return { shown: open, hidden: 0 };
  return { shown: open.slice(0, SHOW_FIRST), hidden: open.length - SHOW_FIRST };
}

const UPLOAD_LABELS: Record<string, string> = {
  wida: 'Access',
  wida_alt: 'Alternate ACCESS',
  roster: 'Roster',
};

function yearSpan(schoolYear: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(schoolYear);
  if (!m) return schoolYear;
  return `${m[1]}-${m[1]!.slice(0, 2)}${m[2]}`;
}

export function sourceLabel(record: MatchRecord): string {
  const { source } = record;
  if (source.kind === 'feed') return 'SIS feed';
  const base = UPLOAD_LABELS[source.uploadKind ?? ''] ?? 'Uploaded file';
  if (source.schoolYear) return `${base} ${yearSpan(source.schoolYear)}`;
  return source.fileName ? `${base} · ${source.fileName}` : base;
}

export function nameSourceLabel(record: MatchRecord): string {
  const columns = record.source.nameColumns ?? [];
  return columns.length > 0 ? `Name from ${columns.join(' + ')}` : '';
}

function trim(value: string | undefined): string {
  return (value ?? '').trim();
}

export function recordName(record: MatchRecord): string {
  return [trim(record.firstName), trim(record.lastName)].filter(Boolean).join(' ');
}

function sharedAcross(records: MatchRecord[], pick: (record: MatchRecord) => string): boolean {
  const ids = records.map(pick);
  return ids.length > 1 && ids[0] !== '' && ids.every((id) => id === ids[0]);
}

function idsOf(record: MatchRecord): string[] {
  return [record.stateStudentId, record.districtStudentId, ...(record.stateAliases ?? []), ...(record.districtAliases ?? [])]
    .map(trim)
    .filter((id) => id !== '');
}

export function recordId(record: MatchRecord, records: MatchRecord[] = []): string {
  const state = trim(record.stateStudentId);
  const district = trim(record.districtStudentId);
  const sharedState = sharedAcross(records, (r) => trim(r.stateStudentId));
  const sharedDistrict = sharedAcross(records, (r) => trim(r.districtStudentId));
  if (district !== '' && sharedDistrict && !sharedState) {
    return district;
  }
  if (records.length > 1 && !sharedState && !sharedDistrict) {
    const shared = idsOf(record).find((id) => records.every((r) => idsOf(r).includes(id)));
    if (shared !== undefined) return shared;
  }
  return state || district;
}

export function matchName(match: PossibleMatch): string {
  for (const record of match.records) {
    const name = recordName(record);
    if (name) return name;
  }
  return 'This student';
}

const DOB_MDY = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
const DOB_YMD = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;

export function displayDob(value: string): string {
  const trimmed = value.trim();
  const mdy = DOB_MDY.exec(trimmed);
  if (mdy) return `${Number(mdy[1])}/${Number(mdy[2])}/${mdy[3]}`;
  const ymd = DOB_YMD.exec(trimmed);
  if (ymd) return `${Number(ymd[2])}/${Number(ymd[3])}/${ymd[1]}`;
  return trimmed;
}

export function matchesLine(n: number): string {
  return `${n} possible ${n === 1 ? 'match' : 'matches'} to review`;
}

export function heldOutLine(n: number): string {
  return `${n} ${n === 1 ? 'student' : 'students'} held out of plans and reports until reviewed`;
}

export function heldOutUntilReviewed(n: number): string {
  return `${n} ${n === 1 ? 'student' : 'students'} held out until reviewed`;
}

export function heldOutShort(n: number): string {
  return `${n} ${n === 1 ? 'student' : 'students'} held out of plans and reports`;
}

export function answerToast(kind: MatchAnswerKind, name: string, remaining: number): string {
  if (remaining === 0) return 'Every match reviewed. Those students are back in plans and reports.';
  if (kind === 'keep_separate') return 'Records kept separate.';
  return `Records combined. ${name} now has one record.`;
}

export type SetAsideCopy = { title: string; body: string; contact: boolean };

export function setAsideCopy(file: SetAsideFile): SetAsideCopy {
  if (file.against === 'feed' && file.case === 'sis_local_ids') {
    return {
      title: 'Your SIS is sending local student IDs instead of state IDs',
      body: `Your ACCESS results use state IDs, so only ${file.matched} of ${file.total} students match, and ${file.fileName} isn’t being used until this is fixed. Ask Flashlight to switch your SIS connection to state student IDs.`,
      contact: true,
    };
  }
  if (file.against === 'feed' && file.case === 'access_ids_wrong') {
    return {
      title: `${file.fileName} isn’t being used`,
      body: `Only ${file.matched} of its ${file.total} students match the state IDs in your student information system. Upload a copy of the file that uses state student IDs.`,
      contact: false,
    };
  }
  if (file.against === 'feed') {
    return {
      title: `${file.fileName} isn’t being used`,
      body: `Only ${file.matched} of its ${file.total} students match your student information system, so it probably uses a different kind of student ID. Upload a copy that uses the same ID as your SIS.`,
      contact: false,
    };
  }
  return {
    title: `${file.fileName} isn’t being used`,
    body: `Only ${file.matched} of its ${file.total} students match your other data, so it probably uses a different kind of student ID. Upload a copy that uses the same ID as your other files.`,
    contact: false,
  };
}

export function diffChars(a: string, b: string): { a: number[]; b: number[] } {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    const row = dp[i]!;
    const rowBelow = dp[i + 1]!;
    for (let j = n - 1; j >= 0; j--) {
      row[j] = a[i] === b[j] ? rowBelow[j + 1]! + 1 : Math.max(rowBelow[j]!, row[j + 1]!);
    }
  }
  const matchedA = new Set<number>();
  const matchedB = new Set<number>();
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) {
      matchedA.add(i);
      matchedB.add(j);
      i++;
      j++;
    } else if (dp[i + 1]![j]! >= dp[i]![j + 1]!) {
      i++;
    } else {
      j++;
    }
  }
  const da: number[] = [];
  const db: number[] = [];
  for (let k = 0; k < m; k++) if (!matchedA.has(k)) da.push(k);
  for (let k = 0; k < n; k++) if (!matchedB.has(k)) db.push(k);
  return { a: da, b: db };
}

export type DiffSegment = { text: string; diff: boolean };

export function diffSegments(values: string[]): DiffSegment[][] {
  const differing = values.map(() => new Set<number>());
  const reference = values[0];
  if (reference !== undefined) {
    for (let i = 1; i < values.length; i++) {
      const { a, b } = diffChars(reference, values[i]!);
      for (const k of a) differing[0]!.add(k);
      for (const k of b) differing[i]!.add(k);
    }
  }
  return values.map((value, i) => toSegments(value, differing[i]!));
}

function toSegments(value: string, differing: Set<number>): DiffSegment[] {
  const segments: DiffSegment[] = [];
  for (let i = 0; i < value.length; i++) {
    const diff = differing.has(i);
    const last = segments[segments.length - 1];
    if (last && last.diff === diff) last.text += value[i];
    else segments.push({ text: value[i]!, diff });
  }
  return segments;
}
